import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { Logger } from '@utils/logger';
import { Router } from '@angular/router';
import { ViewModeService } from '@services/view-mode.service';

/**
 * Logger
 */
const logger = Logger.getLogger('ImportantLegalComponent');

/**
 * Interface for the RichText Content
 */
interface RichTextContent {
  content: RichTextValue;
}

interface RichTextValue {
  value: string;
}

/**
 * FT Rich Text component
 *
 * This component is used at the top of a template and has it's own associated document type.
 *
 * Selector: `ft-important-legal`
 *
 * Exported as: `ImportantLegalComponent`
 */
@Component({
  selector: 'ft-important-legal',
  templateUrl: './important-legal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportantLegalComponent
  extends AbstractBaseComponent
  implements OnInit {
  /**
   * Url of Image ref
   */
  imageUrl: string;

  public transformedHtml: string;
  private footnoteRegex = /\[(\d+)\]/g;

  /**
   * Constructor
   */
  constructor(private viewModeService: ViewModeService) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  /**
   * Angular init hook
   */
  ngOnInit(): void {
    super.ngOnInit();
    const rawHtml = this.content?.content?.value || '';
    this.transformedHtml = this.transformFootnotes(rawHtml);
  }

  /**
   * Get content from document
   */
  get content() {
    return this.document?.getData<RichTextContent>();
  }

  /**
   * Transforms footnotes in HTML content by converting bracketed numbers [1] and numbered text "1. text"
   * into clickable anchor links that scroll back to their corresponding reference points in the document.
   * This enables bidirectional navigation between footnote references and their detailed explanations.
   */
  private transformFootnotes(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const paragraphs = Array.from(doc.querySelectorAll('p'));

    paragraphs.forEach((p) => {
      const small = p.querySelector('small');
      if (!small) {
        return;
      }

      const text = small.textContent?.trim();

      // Case 1: [2] style footnotes
      const bracketMatches = text?.match(this.footnoteRegex);
      if (bracketMatches) {
        bracketMatches.forEach((footnote) => {
          const footnoteNumber = footnote.replace(/\[|\]/g, '');
          const anchor = `<a href="javascript:void(0)" onclick="document.getElementById('fnref${footnoteNumber}')?.scrollIntoView({ behavior: 'smooth', block: 'center' })" id="fn${footnoteNumber}" class="strategy-data__footnotes-src padding-right--xs">${footnoteNumber}</a>`;
          small.innerHTML = small.innerHTML.replace(footnote, anchor);
        });
      }

      // Case 2: "2. Some text" style footnotes
      const numberedMatch = text?.match(/^(\d+)\.\s+(.*)/);
      if (numberedMatch) {
        const footnoteNumber = numberedMatch[1];
        const rest = numberedMatch[2];
        const anchor = `<a href="javascript:void(0)" onclick="document.getElementById('fnref${footnoteNumber}')?.scrollIntoView({ behavior: 'smooth', block: 'center' })" id="fn${footnoteNumber}" class="strategy-data__footnotes-src padding-right--xs">${footnoteNumber}</a>`;
        small.innerHTML = `${anchor} ${rest}&nbsp;`;
      }
    });

    return doc.body.innerHTML;
  }

  scrollToTarget(id: string): void {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
